<script setup>
  const props = defineProps({
    publication: {
      required: false,
      type: Object,
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
  })

  const { $dayjs } = useNuxtApp()
  const { locale } = useI18n()
  const countries = useState("countries")
  const minProfiles = 2

  const getFilteredDates = computed(() => {
    /*if (stepStore.countryZoneSelected && !stepStore.countryZoneCitySelected) {
    return props.publication?.dates.filter(date => date?.country_zone_id === stepStore.countryZoneSelected)
  }

  if (stepStore.countryZoneSelected && stepStore.countryZoneCitySelected) {
    return props.publication?.dates.filter(date => {
      return date?.country_zone_id === stepStore.countryZoneSelected &&
        date?.country_zone_city_id === stepStore.countryZoneCitySelected
    })
  }*/

    return props.publication?.dates
  })

  const isSingleDatePublication = computed(() => {
    return getFilteredDates.value?.length === 1
  })

  const formatStartDate = (startDate) => {
    return $dayjs(startDate).format("dddd D MMMM")
  }

  const formatEndDate = (endDate) => {
    return $dayjs(endDate).format("dddd D [de] MMMM")
  }

  const getArtistQtyResumed = computed(() => {
    const artists = props.publication?.artists
    if (artists.length > minProfiles) {
      return `y ${artists.length - minProfiles} más...`
    }

    return ""
  })

  const getMinArtists = computed(() => {
    const artists = props.publication?.artists
    const minTreeArtists = []

    artists.forEach((artist) => {
      if (minTreeArtists.length < minProfiles) {
        minTreeArtists.push(artist)
      }
    })

    return minTreeArtists
  })

  const getProducerQtyResumed = computed(() => {
    const producers = props.publication?.producers
    if (producers.length > minProfiles) {
      return `y ${producers.length - minProfiles} más...`
    }

    return ""
  })

  const getMinProducers = computed(() => {
    const producers = props.publication?.producers
    const minTreeProducers = []

    producers.forEach((artist) => {
      if (minTreeProducers.length < minProfiles) {
        minTreeProducers.push(artist)
      }
    })

    return minTreeProducers
  })

  const goToPublicationDetail = (publication) => {
    if (getFilteredDates.value.length === 1) {
      return `/${locale.value}/${publication?.slug}-tickets/${getFilteredDates.value[0].id}`
    } else {
      return `/${locale.value}/${publication?.slug}-tickets`
    }
  }

  const isFeaturedPublication = (publication) => {
    return publication.countries.some(
      (country) =>
        countries.value?.some((c) => c.id === country.country_id) &&
        country?.is_featured
    )
  }

  const isYearIsDifferentGetNextYear = (date) => {
    if ($dayjs().isSame(date, "year")) {
      return ""
    } else {
      return $dayjs(date).format("YYYY")
    }
  }
</script>
<template>
  <div class="publication-item" v-if="isLoading">
    <div class="publication-item-header">
      <div class="flex items-center relative">
        <Skeleton shape="circle" size="25px" />
        <div class="flex items-center overlapping">
          <Skeleton class="ml-1.5" width="8rem" />
        </div>
      </div>
    </div>
    <div class="publication-item-image">
      <Skeleton class="!rounded-none" width="100%" height="100%" />
    </div>
    <div class="publication-item-info">
      <Skeleton width="9rem" height="1rem" />
      <div class="dateinfo">
        <div>
          <Skeleton width="12rem" height="1rem" />
          <Skeleton class="mt-1.5 opacity-70" width="4rem" height=".6rem" />
        </div>
        <div>
          <Skeleton width="100%" height="43px" />
        </div>
      </div>
    </div>
  </div>
  <NuxtLink
    v-else
    class="publication-item"
    :to="goToPublicationDetail(publication)"
  >
    <div
      class="publication-item-header h-14"
      v-if="
        props.publication?.artists?.length > 0 ||
        props.publication?.producers?.length > 0 ||
        props.publication?.producers?.length == 0
      "
    >
      <div
        class="flex items-center relative"
        v-if="props.publication?.artists?.length > 0"
      >
        <div
          v-for="(artist, artistIndex) in getMinArtists"
          :key="artistIndex"
          class="relative w-8"
          :style="{ marginLeft: `${artistIndex * -5}px` }"
        >
          <img
            class="rounded-full border-2 border-gray-100"
            :src="artist.photo_url"
            :alt="artist.username"
          />
          <CheckDecagramIcon
            v-if="artist?.is_verified"
            class="absolute bottom-0 right-[-4px] w-[14px]"
          />
        </div>
        <div
          class="flex items-center ml-2"
          :title="`${getMinArtists
            .map((artist) => artist.name)
            .join(', ')} ${getArtistQtyResumed}`"
        >
          <div class="truncate max-w-[200px] 2xl:max-w-[240px] ml-1.5">
            {{ getMinArtists.map((artist) => artist.name).join(", ") }}
            {{ getArtistQtyResumed }}
          </div>
        </div>
      </div>
      <div
        class="flex items-center relative"
        v-if="props.publication?.artists?.length <= 0"
      >
        <div
          v-for="(producer, producerIndex) in getMinProducers"
          :key="producerIndex"
          class="relative w-8"
          :style="{ marginLeft: `${producerIndex * -5}px` }"
        >
          <img
            class="rounded-full border-2 border-gray-100"
            :src="producer.photo_url"
            :alt="producer.username"
          />
          <CheckDecagramIcon
            v-if="producer?.is_verified"
            class="absolute bottom-0 right-[-4px] w-[14px]"
          />
        </div>
        <div
          class="flex items-center ml-2"
          :title="`${getMinProducers
            .map((artist) => artist.name)
            .join(', ')} ${getProducerQtyResumed}`"
        >
          <div class="truncate max-w-[200px] 2xl:max-w-[240px] ml-1.5">
            {{ getMinProducers.map((artist) => artist.name).join(", ") }}
            {{ getProducerQtyResumed }}
          </div>
        </div>
      </div>
      <!-- <div>
        <div class="bg-amber-300 px-2.5 py-0.5 rounded-full text-[11px] uppercase font-bold text-gray-900">
          20% Off
        </div>
      </div>-->
    </div>
    <div
      class="publication-item-image"
      :style="{ backgroundImage: `url(${publication?.cover_image_url})` }"
    />
    <div class="publication-item-info">
      <div class="flex gap-2">
        <h2 class="text-lg font-bold leading-5 line-clamp-2 mb-2">
          {{ publication?.title }}
        </h2>
        <svg
          v-if="isFeaturedPublication(publication)"
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 text-yellow-300"
          fill="currentColor"
          stroke="black"
          viewBox="0 0 24 24"
        >
          <path
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          />
        </svg>
      </div>
      <div class="dateinfo">
        <div>
          <p
            class="text-[15px] leading-5 line-clamp-2"
            v-if="publication?.sub_title"
          >
            {{ publication?.sub_title }}
          </p>
          <div class="text-[13px] line-clamp-2 mt-1.5 opacity-70">
            <div v-if="isSingleDatePublication">
              {{ publication.dates[0].location }}
            </div>
            <div v-else>
              {{ formatStartDate(publication.dates[0].start_date) }} al<br />{{ formatEndDate(publication.dates[publication.dates.length - 1].end_date) }}
              {{ isYearIsDifferentGetNextYear(publication.dates[publication.dates.length - 1].end_date) }}
            </div>
            <div v-if="isSingleDatePublication">
              {{ formatStartDate(publication.dates[0].start_date) }}
            </div>
          </div>
        </div>
        <div>
          <button>Ver Más</button>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped>
  .publication-item {
    border-radius: 1rem;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    background-color: rgba(255, 255, 255, 0.1);

    .publication-item-image {
      height: 350px;
      background-size: cover;
      background-position: center;
    }

    .publication-item-header {
      padding: 0.8rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .publication-item-info {
      width: 100%;
      height: auto;
      padding: 1.5rem;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      button {
        padding: 0.6rem 2rem;
        background-color: #1c1c1c;
        border: none;
        border-radius: 0.6rem;
        color: white;
        cursor: pointer;
        width: 100%;
      }
      .dateinfo {
        display: grid;
        grid-template-columns: 2fr 130px;
        gap: 0.5rem;
        align-items: end;
      }
    }
  }
</style>
